import type { SxProps, Theme } from "@mui/material";
import { alpha, lighten } from "@mui/material";
import type { FeatureFlags } from "@/contexts/FeaturesContext";

/**
 * Returns the MUI sx styles for a given "alternative dates" calendar variant.
 * These are passed to DateRangeCalendar's sx prop and override its base styles.
 */
export function getAlternativeSx(
  variant: FeatureFlags["periodSelection"],
  theme: Theme,
  strikethroughDisabled: boolean,
): SxProps<Theme> {
  const disabledSx = {
    "& .rdp-day.rdp-disabled": {
      backgroundColor: "white",
      fontWeight: "normal",
      color: lighten(theme.palette.text.disabled, 0.3),
      ...(strikethroughDisabled && { textDecoration: "line-through" }),
    },
  };

  // Shared pill-button layout used by variants A and I
  const pillSx = {
    "& .rdp-day_button": { borderRadius: "10px", width: "45px", height: "20px", margin: "0 auto" },
    "& .rdp-selected": {
      fontSize: theme.typography.caption.fontSize,
      textDecoration: "none !important",
      fontWeight: "600 !important",
    },
    "& .rdp-range_start .rdp-day_button": { paddingLeft: "2px" },
    "& .rdp-range_middle": { borderRadius: "0%", fontWeight: 600 },
    "& .rdp-range_end": { fontWeight: 600 },
    "& .rdp-range_middle .rdp-day_button, & .hoverRangeMiddle .rdp-day_button": {
      backgroundColor: theme.palette.success.main,
      color: theme.palette.success.contrastText,
      width: "100%",
      borderRadius: "0%",
    },
    "& .rdp-range_start, & .hoverRangeStart": {
      backgroundImage: `linear-gradient(to bottom, ${theme.palette.success.main}, ${theme.palette.success.main})`,
      backgroundSize: "100% 20px",
      backgroundPosition: "22px 50%",
      backgroundRepeat: "no-repeat",
    },
    "& .rdp-range_end, & .hoverRangeEnd": {
      background: "none",
      backgroundImage: `linear-gradient(to bottom, ${theme.palette.success.main}, ${theme.palette.success.main})`,
      backgroundSize: "50% 20px",
      backgroundPosition: "0 50%",
      backgroundRepeat: "no-repeat",
    },
    "& .rdp-range_start.hoverRangeStart.rdp-range_end": {
      background: "none",
      backgroundImage: `linear-gradient(to bottom, ${theme.palette.success.main}, ${theme.palette.success.main})`,
      backgroundSize: "100% 20px",
      backgroundPosition: "22px 50%",
      backgroundRepeat: "no-repeat",
    },
    "& .rdp-day": {
      padding: 0,
      color: theme.palette.success.dark,
      backgroundColor: alpha(theme.palette.success.main, 0.15),
      fontWeight: "bold",
    },

    ...disabledSx,
  };

  if (variant === "month-view-b") return pillSx;

  if (variant === "month-view-c") {
    return {
      "& .rdp-day": { padding: 0, color: theme.palette.success.dark, fontWeight: "bold" },
      ...disabledSx,
      "& .rdp-selected": {
        fontSize: theme.typography.caption.fontSize,
        textDecoration: "none !important",
        fontWeight: "600 !important",
      },
      "& .rdp-range_middle": { background: "none", borderRadius: "0%" },
    };
  }

  if (variant === "airbnb") {
    return {
      "& .rdp-day": { padding: 0, color: theme.palette.success.dark, fontWeight: "bold" },
      "& .rdp-selected .rdp-day_button": {
        background: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        borderColor: theme.palette.success.main,
      },
      // "& .rdp-selected.rdp-disabled": { textDecoration: "none" },
      ...disabledSx,
      "& .rdp-range_middle .rdp-day_button": {
        backgroundColor: theme.palette.background.default,
        color: theme.palette.success.dark,
        width: "100%",
        borderRadius: "0%",
      },
      "& .rdp-range_middle": { background: "none !important", borderRadius: "0%" },
    };
  }

  if (variant === "month-view-default") {
    return {
      ...disabledSx,
      "& .rdp-day_button": {
        width: "28px",
        height: "28px",
      },
      "& .rdp-day": {
        padding: 0,
        color: theme.palette.success.dark,
        backgroundColor: alpha(theme.palette.success.main, 0.15),
        fontWeight: "bold",
        border: "3px solid white",
      },
      "& .rdp-day::after": {
        content: '""',
        display: "block",
        width: "4px",
        height: "4px",
        borderRadius: "50%",
        backgroundColor: theme.palette.success.dark,
        margin: "-6px auto 0",
      },
      "& .rdp-day.rdp-selected::after": {
        backgroundColor: "white",
      },
      "& .rdp-day.validStay": {
        backgroundColor: alpha(theme.palette.success.main, 0.1),
        color: alpha(theme.palette.success.main, 0.3),
        textDecoration: "none",
      },
      "& .rdp-day.validStay::after": {
        content: "none",
      },
      "& .rdp-day.stayDaySelected": {
        outline: `1px solid ${theme.palette.success.dark}`,
        outlineOffset: "-2px",
        backgroundColor: alpha(theme.palette.success.main, 0.1),
        color: alpha(theme.palette.success.main, 0.3),
      },
      "& .rdp-day.stayDaySelected::after": {
        content: "none",
      },
      "& .rdp-day.unavailable": {
        backgroundColor: "white",
        color: lighten(theme.palette.text.disabled, 0.3),
        fontWeight: "normal",
        cursor: "default",
        ...(strikethroughDisabled && { textDecoration: "line-through" }),
      },
      "& .rdp-day.unavailable::after": {
        content: "none",
      },
      "& .rdp-selected": {
        background: `${theme.palette.success.main} !important`,
        border: "none",
      },
      "& .rdp-range_middle .rdp-day_button, & .hoverRangeMiddle .rdp-day_button": {
        backgroundColor: theme.palette.success.main,
        color: theme.palette.success.contrastText,
        width: "100%",
        borderRadius: "0%",
      },
    };
  }

  // Default — month-view-a and any future variant
  return {
    "& .rdp-day": {
      color: theme.palette.success.dark,
      backgroundColor: "#F0F3EF",
      fontWeight: "bold",
    },
    ...disabledSx,
    "& .rdp-range_middle .rdp-day_button": { backgroundColor: "inherit" },
    "& .rdp-range_end, & .hoverRangeEnd": { background: "none" },
    "& .rdp-range_end .rdp-day_button": {
      background: "none",
      border: "none",
      color: lighten(theme.palette.text.disabled, 0.3),
    },
    "& .rdp-range_start, & .hoverRangeStart": {
      background: "none",
      backgroundColor: `${theme.palette.success.main} !important`,
    },
    "& .rdp-range_start .rdp-day_button": {
      background: "none",
      border: "none",
      color: theme.palette.success.contrastText,
    },
  };
}
